import { Plus, Trash01 } from '@untitled-ui/icons-react/build/cjs';
import React, { useState } from 'react';
import ButtonCmp from 'src/components/ButtonCmp';
import InputText from 'src/components/InputText/InputText';
import SelectBox from 'src/components/SelectBox/SelectBox';
import TooltipCmp from 'src/components/TooltipCmp';

import AiCommonModal from './AiCommonModal';

const UNIT_TYPE_OPTIONS = [
    { label: 'Pallet', value: 'pallet' },
    { label: 'Box', value: 'box' },
    { label: 'Crate', value: 'crate' },
    { label: 'Drum', value: 'drum' },
    { label: 'Bundle', value: 'bundle' },
];

export interface IPackagingLineItem {
    unitType: string;
    noOfHU: string;
    length: string;
    width: string;
    height: string;
    unitWeight: string;
    totalWeight: string;
    freightClass: string;
    description: string;
}

const EMPTY_LINE_ITEM: IPackagingLineItem = {
    unitType: 'pallet',
    noOfHU: '',
    length: '',
    width: '',
    height: '',
    unitWeight: '',
    totalWeight: '',
    freightClass: '',
    description: '',
};

const REQUIRED_FIELDS: (keyof IPackagingLineItem)[] = ['noOfHU', 'length', 'width', 'height', 'unitWeight', 'totalWeight'];

const getMissingFields = (item: IPackagingLineItem) => REQUIRED_FIELDS.filter((field) => !String(item[field]).trim());

interface IProps {
    onConfirm: (lineItems: IPackagingLineItem[]) => void;
    onBack: () => void;
    onClose: () => void;
}

const PackagingDetailsCard = ({ onConfirm, onBack, onClose }: IProps) => {
    const [lineItems, setLineItems] = useState<IPackagingLineItem[]>([{ ...EMPTY_LINE_ITEM }]);
    const [activeIndex, setActiveIndex] = useState(0);
    const [attemptedSubmit, setAttemptedSubmit] = useState(false);

    const activeItem = lineItems[activeIndex];
    const missingFields = attemptedSubmit ? getMissingFields(activeItem) : [];

    const updateLineItem = (field: keyof IPackagingLineItem, value: string) => {
        setLineItems((prev) => prev.map((item, i) => (i === activeIndex ? { ...item, [field]: value } : item)));
    };

    const handleAddLineItem = () => {
        setLineItems((prev) => [...prev, { ...EMPTY_LINE_ITEM }]);
        setActiveIndex(lineItems.length);
        setAttemptedSubmit(false);
    };

    const handleRemoveLineItem = () => {
        if (lineItems.length <= 1) return;

        setLineItems((prev) => prev.filter((_, i) => i !== activeIndex));
        setActiveIndex((prev) => Math.min(prev, lineItems.length - 2));
        setAttemptedSubmit(false);
    };

    const goToItem = (index: number) => {
        setActiveIndex(index);
        setAttemptedSubmit(false);
    };

    const isLastItem = activeIndex === lineItems.length - 1;

    const handleConfirm = () => {
        const firstInvalidIndex = lineItems.findIndex((item) => getMissingFields(item).length > 0);

        if (firstInvalidIndex !== -1) {
            setActiveIndex(firstInvalidIndex);
            setAttemptedSubmit(true);

            return;
        }

        onConfirm(lineItems);
    };

    const handlePrimaryClick = () => {
        if (isLastItem) {
            handleConfirm();

            return;
        }

        if (missingFields.length > 0) {
            setAttemptedSubmit(true);

            return;
        }

        goToItem(activeIndex + 1);
    };

    const handleSecondaryClick = () => {
        if (activeIndex === 0) {
            onBack();

            return;
        }

        goToItem(activeIndex - 1);
    };

    return (
        <AiCommonModal
            title={
                lineItems.length > 1 ? (
                    <div className="flex items-center justify-between w-full">
                        <span>How is your shipment packaged?</span>
                        <span className="text-gray400 text-xs font-normal">
                            {activeIndex + 1}/{lineItems.length}
                        </span>
                    </div>
                ) : (
                    'How is your shipment packaged?'
                )
            }
            agent="kate"
            handleClose={onClose}
            footer={
                <div className="flex items-center gap-x-2">
                    <TooltipCmp message="Add Line Item">
                        <ButtonCmp type="button" size="square-xs" className="btn-outline-primary" onClick={handleAddLineItem}>
                            <Plus className="w-3.5 h-3.5" />
                        </ButtonCmp>
                    </TooltipCmp>
                    <TooltipCmp message="Delete Line Item">
                        <ButtonCmp type="button" size="square-xs" className="btn-outline-primary" disabled={lineItems.length <= 1} onClick={handleRemoveLineItem}>
                            <Trash01 className="w-3.5 h-3.5" />
                        </ButtonCmp>
                    </TooltipCmp>
                </div>
            }
            secondaryBtnText="Back"
            secondaryBtnOnClick={handleSecondaryClick}
            primaryBtnText={isLastItem ? 'Confirm' : 'Next'}
            primaryBtnOnClick={handlePrimaryClick}
        >
            <p className="text-gray500 text-xs leading-[1.5] font-normal mb-3">Choose whether your shipment is on pallets, in boxes, crates, along with their dimensions and total weight.</p>

            <div className="space-y-2">
                <div className="grid grid-cols-2 gap-2">
                    <SelectBox
                        name="unitType"
                        id="unitType"
                        options={UNIT_TYPE_OPTIONS}
                        value={UNIT_TYPE_OPTIONS.find((o) => o.value === activeItem.unitType)}
                        onChangeFunc={(opt: any) => updateLineItem('unitType', opt?.value ?? '')}
                        isSearchable={false}
                    />
                    <InputText
                        inputType="number"
                        placeholder="N° of HU"
                        value={activeItem.noOfHU}
                        isError={missingFields.includes('noOfHU')}
                        onChangeFunc={(e) => updateLineItem('noOfHU', e.target.value)}
                    />
                </div>
                <div className="grid grid-cols-3 gap-2">
                    <InputText
                        inputType="number"
                        placeholder="L (in)"
                        value={activeItem.length}
                        isError={missingFields.includes('length')}
                        onChangeFunc={(e) => updateLineItem('length', e.target.value)}
                    />
                    <InputText
                        inputType="number"
                        placeholder="W (in)"
                        value={activeItem.width}
                        isError={missingFields.includes('width')}
                        onChangeFunc={(e) => updateLineItem('width', e.target.value)}
                    />
                    <InputText
                        inputType="number"
                        placeholder="H (in)"
                        value={activeItem.height}
                        isError={missingFields.includes('height')}
                        onChangeFunc={(e) => updateLineItem('height', e.target.value)}
                    />
                </div>
                <div className="grid grid-cols-2 gap-2">
                    <InputText
                        inputType="number"
                        placeholder="Unit weight (lbs)"
                        value={activeItem.unitWeight}
                        isError={missingFields.includes('unitWeight')}
                        onChangeFunc={(e) => updateLineItem('unitWeight', e.target.value)}
                    />
                    <InputText
                        inputType="number"
                        placeholder="Total weight (lbs)"
                        value={activeItem.totalWeight}
                        isError={missingFields.includes('totalWeight')}
                        onChangeFunc={(e) => updateLineItem('totalWeight', e.target.value)}
                    />
                </div>
                <div className="grid grid-cols-2 gap-2">
                    <InputText placeholder="FC" value={activeItem.freightClass} onChangeFunc={(e) => updateLineItem('freightClass', e.target.value)} />
                    <InputText placeholder="Enter Description" value={activeItem.description} onChangeFunc={(e) => updateLineItem('description', e.target.value)} />
                </div>
            </div>
        </AiCommonModal>
    );
};

export default PackagingDetailsCard;
